<script lang="ts">
  import CinemaTile from "./cinema-tile/CinemaTile.svelte";
  import { logError } from "../../../logger.ts";
  import type {
    TrackingFeature,
    TrackingLabels,
  } from "../tracking/trackingUtils.ts";
  import {
    buildCinemaFeature,
    buildCinemaFeatureForScroll,
    buildTileFeature,
    castTrackingFeaturesToFeatures,
    extractBaseLabels,
  } from "../tracking/trackingUtils.ts";
  import {
    submitEventMerge,
    submitEvent,
    submitMerge,
  } from "@otto-ec/tracking-bct";
  import { addLabels } from "@otto-ec/global-resources/apm";
  import { setContext } from "svelte";
  import cinemaTilePromise from "./cinemaTilePromiseKey.ts";

  const {
    variations = [],
    onAllProductTilesFailed = () => {},
    auctionId = "",
    cinemaTrackingLabels = {} as TrackingLabels,
    eventMergeId = "",
  } = $props();

  const defaultArrowPosition = "50%";

  let cinemaElement = $state<HTMLElement | undefined>();
  let arrowButtonPosition = $state(defaultArrowPosition);
  let failedProductTileCount = 0;

  const promises: Promise<void>[] = [];

  setContext<(p: Promise<void>) => void>(cinemaTilePromise, (p) => {
    promises.push(p);
  });

  addLabels({ hopper_auction_cinema_basic_initial: "true" });

  $effect(() => {
    Promise.all(promises).then(() => {
      addLabels({ hopper_auction_cinema_basic_rendered: "true" }); /*    */
    });
  });

  $effect(() => {
    /*               */
    if (eventMergeId) {
      Promise.all(promises).then(() => {
        addLabels({ hopper_auction_cinema_basic_variation_change: "true" }); /*  */
        /*                                                                          */
        const baseLabels = extractBaseLabels(getVisibleTileFeatures());
        submitEventMerge(eventMergeId, baseLabels);
      });
    } else {
      /*                          */
      Promise.all(promises).then(() => {
        addLabels({ hopper_auction_cinema_basic_normal_load: "true" }); /*    */
        const baseLabels = extractBaseLabels(getVisibleTileFeatures());
        const features = getVisibleTileFeatures();
        submitMerge(baseLabels, castTrackingFeaturesToFeatures(features));
      });
    }
  });

  /*                                                                                 */
  $effect(() => {
    const cinema = cinemaElement;
    if (!cinema) return;

    const updatePosition = () => {
      const productImage = cinema.querySelector<HTMLElement>(
        ".cinema-product-image",
      );

      if (productImage) {
        arrowButtonPosition = `${productImage.offsetTop + productImage.clientHeight / 2}px`;
      }
    };

    /*                                                                                       */
    const observer = new ResizeObserver(updatePosition);
    observer.observe(cinema);

    /*                                                                              */
    return () => observer.disconnect();
  });

  function getVisibleTileFeatures(): TrackingFeature[] {
    const cinemaBounds = cinemaElement!.getBoundingClientRect();
    const visibleTiles = variations
      .map((variation, index) => ({ variation, position: index }))
      .filter(({ position }) => {
        const tile = cinemaElement!.children[position] as
          | HTMLElement
          | undefined;
        if (!tile) return false;
        const tileBounds = tile.getBoundingClientRect();
        return (
          tileBounds.right > cinemaBounds.left &&
          tileBounds.left < cinemaBounds.right
        );
      });

    return [
      buildCinemaFeature(auctionId, cinemaTrackingLabels),
      ...visibleTiles.map(({ variation, position }) =>
        buildTileFeature(
          auctionId,
          variation.variationId,
          position + 1,
          variation.tileTrackingLabels as TrackingLabels,
        ),
      ),
    ];
  }

  type CinemaScrollEvent = CustomEvent<{
    visibleItems: { indices: number[] };
    scrollDirection: "right" | "left";
  }>;

  function handleScrollCompleted(event: Event) {
    const scrollEvent = event as CinemaScrollEvent;
    const { visibleItems, scrollDirection } = scrollEvent.detail;

    const actionName = scrollDirection === "right" ? "next" : "previous";

    const visibleVariations = visibleItems.indices
      .map((index) => ({ variation: variations[index], position: index + 1 }))
      .filter(({ variation }) => !!variation);

    const features: TrackingFeature[] = [
      buildCinemaFeatureForScroll(auctionId, cinemaTrackingLabels),
      ...visibleVariations.map(({ variation, position }) =>
        buildTileFeature(
          auctionId,
          variation.variationId,
          position,
          variation.tileTrackingLabels as TrackingLabels,
        ),
      ),
    ];

    const baseLabels = extractBaseLabels(features);
    const convertedFeatures = castTrackingFeaturesToFeatures(features);
    submitEvent?.(baseLabels, {
      features: convertedFeatures,
      name: actionName,
    });
  }
</script>

<oc-cinema-v1
  class="basic-cinema"
  bind:this={cinemaElement}
  oc-aria-label="Gesponserte Produkte"
  arrow-button-position={arrowButtonPosition}
  gap="8px"
  onoc-stage-scroll-completed={handleScrollCompleted}
>
  {#each variations as variation, index}
    <svelte:boundary
      onerror={(error) => {
        logError(
          Error(`CinemaTile ${index} failed to render.`, { cause: error }),
        );
        failedProductTileCount++;
        if (failedProductTileCount >= variations.length)
          onAllProductTilesFailed();
      }}
    >
      <CinemaTile
        {variation}
        lazyLoad={index > 4}
        {auctionId}
        position={index + 1}
        {cinemaTrackingLabels}
      />
      {#snippet failed()}{/snippet}
    </svelte:boundary>
  {/each}
</oc-cinema-v1>

<style lang="scss">
  .basic-cinema {
    --items-per-page: 2;
  }

  /*                                                                           */
  @container (width > 767px) {
    .basic-cinema {
      --items-per-page: 5;
    }
  }

  @container ((width > 636px) and (width <= 767px)) {
    .basic-cinema {
      --items-per-page: 4;
    }
  }

  @container ((width >= 497px) and (width <= 636px)) {
    .basic-cinema {
      --items-per-page: 3;
    }
  }

  @container (width < 497px) {
    .basic-cinema {
      --items-per-page: 2;
    }
  }
</style>
